import  { useEffect, useMemo, useState } from 'react';
import TableContainer from 'Common/TableContainer';
import { Dropdown } from 'Common/Components/Dropdown';
import Select from "react-select";
import { MoreHorizontal, FileEdit, Trash2, Plus } from 'lucide-react';
import Modal from 'Common/Components/Modal';
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import {
    getTransferList,
    getWalletList,
    addTransfer
} from "../../helpers/fakebackend_helper";
import { ToastContainer } from 'react-toastify';

interface TransferDTO {
  fromId: number;
  toId: number;
  description: string;
  amount: number;
}

const TransferList = () => {
  const [data, setData] = useState<TransferDTO[]>([]);
  const [eventData, setEventData] = useState<TransferDTO | null>(null);
  const [show, setShow] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const [walletOptions, setWalletOptions] = useState<
    { value: number; label: any, balance : number }[]
  >([]);

  const fetchTransfers = async () => {
    showLoader();

    try {
      const params = {
        page: 0,
        size: 1000,
        sort: "transferId,desc",
      };
      const response = await getTransferList(params);
      const wallets = response.data?._embedded?.transfers || [];
      setData(wallets);
    } catch (error) {
      toast.error("Error fetching transfers: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const response = await getWalletList();
        const embedded = response.data?._embedded?.wallets;
        const options = embedded
          ? Object.values(embedded).map((wallet: any) => ({
              value: wallet.walletId,
              label: String(wallet.name + " - Saldo: " + wallet.balance.toFixed(2)),
              balance: wallet.balance
            }))
          : [];
          setWalletOptions(options);
      } catch (error) {
        toast.error("Error al obtener las carteras: " + error, { autoClose: 2000 });
      }
    };
    fetchWallets();
  }, []);

  useEffect(() => {
    fetchTransfers();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
        fromId: 0,
        toId: 0,
        description: "",
        amount: 0,
    },
    validationSchema: Yup.object({
      fromId: Yup.number().required("Cartera origen es requerida"),
      toId: Yup.number()
        .required("Cartera destino es requerida")
        .notOneOf([Yup.ref("fromId")], "No puede transferir a la misma cuenta"),
      description: Yup.string().required("Descripción es requerida"),
      amount: Yup.number()
        .required("Monto es requerido")
        .moreThan(0, "El monto debe ser mayor a 0")
        .test("sufficient-balance", "Saldo insuficiente en la cuenta origen", function (value) {
          const { fromId } = this.parent;
          const selectedWallet = walletOptions.find(opt => opt.value === fromId);
          if (!selectedWallet || value == null) return true;
          return selectedWallet.balance >= value;
        }),
    }),

    onSubmit: async (values) => {
      showLoader();

      try {
          await addTransfer(values);
          toast.success("Transaccion procesada correctamente", { autoClose: 2000 });
        handleCloseModal();
        fetchTransfers();
      } catch (error) {
        toast.error("Error saving transfer: " + error, { autoClose: 2000 });
      } finally {
        hideLoader();
      }
    }
  });

  const handleCloseModal = () => {
    setShow(false);
    setEventData(null);
    formik.resetForm();
  };

  const handleCreateClick = () => {
    setEventData(null);
    formik.resetForm();
    setShow(true);
  };

  const onClickDelete = (cell: TransferDTO) => {
    setEventData(cell);
  };

  const handleUpdateDataClick = (ele: TransferDTO) => {
    setEventData(ele);
    setShow(true);
  };

  const columns = useMemo(() => [
    {
      header: "Desde esta cuenta",
      accessorKey: "from.name",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Hacia esta cartera",
      accessorKey: "to.name",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Monto",
      accessorKey: "amount",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ cell }: any) => `Q${cell.getValue()}`,
    },
    {
      header: "Descripción",
      accessorKey: "description",
      enableColumnFilter: false,
      enableSorting: false,
    }
  ], []);

  return (
    <div className="card">
      <ToastContainer closeButton={false} limit={1} />

      <div className="card-body">
        <div className="flex justify-end mb-4">
          <button
            onClick={handleCreateClick}
            className="btn bg-custom-500 text-white"
          >
            <Plus className="inline size-4 mr-1" /> Realizar Transferencia
          </button>
        </div>
      </div>

      <div className="card-body">
        <TableContainer
          isPagination={true}
          isSelect={true}
          isGlobalFilter={true}
          columns={columns}
          data={data}
          customPageSize={5}
          divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
          tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
          theadclassName="border-b border-slate-200 dark:border-zink-500"
          trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
          thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
          tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
        />
      </div>

      <Modal
        show={show}
        onHide={handleCloseModal}
        modal-center
        className="fixed inset-0 z-drawer flex items-center justify-center"
        dialogClassName="w-screen max-w-md bg-white shadow-xl rounded-xl dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between px-6 py-4 border-b dark:border-zink-500">
          <Modal.Title className="text-lg font-semibold text-slate-900 dark:text-zink-100">
            {"Agregar Billetera"}
          </Modal.Title>
          <button
            onClick={handleCloseModal}
            className="text-slate-400 hover:text-red-500 transition"
          >
            ×
          </button>
        </Modal.Header>

        <Modal.Body className="p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div className="md:col-span-2">
                <label
                  htmlFor="fromId"
                  className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                >
                  Desde esta Cuenta <span className="text-red-500">*</span>
                </label>
                <Select
                  name="fromId"
                  value={(() => {
                    const selected = walletOptions.find((opt) => {
                      return opt.value === formik.values.fromId;
                    });
                    return selected;
                  })()}
                  onChange={(selected) => {
                    if (selected) {
                      formik.setFieldValue("fromId", selected.value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  options={walletOptions}
                  className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
                />
                {formik.touched.fromId && formik.errors.fromId && (
                  <p className="text-sm text-red-500 mt-1">
                    {formik.errors.fromId}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="toId"
                  className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100"
                >
                  Hacia esta Cuenta <span className="text-red-500">*</span>
                </label>
                <Select
                  name="toId"
                  value={(() => {
                    const selected = walletOptions.find((opt) => {
                      return opt.value === formik.values.toId;
                    });
                    return selected;
                  })()}
                  onChange={(selected) => {
                    if (selected) {
                      formik.setFieldValue("toId", selected.value);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  options={walletOptions}
                  className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
                />
                {formik.touched.toId && formik.errors.toId && (
                  <p className="text-sm text-red-500 mt-1">
                    {formik.errors.toId}
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="amountInput"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Agregar Fondos <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Ingrese monto"
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.amount && formik.errors.amount ? (
                  <div className="text-red-500">{formik.errors.amount}</div>
                ) : null}
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="inline-block mb-2 text-base font-medium"
                >
                  Descripción
                </label>
                <textarea
                  name="description"
                  className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn bg-white text-red-500 border border-red-500 hover:bg-red-50 transition"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn bg-custom-500 text-white hover:bg-custom-600 transition"
              >
                {"Agregar"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TransferList;